import {
  AudioConfig,
  AudioOutputStream,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
  SpeechSynthesisOutputFormat,
  SpeechSynthesizer,
} from 'microsoft-cognitiveservices-speech-sdk';
import React, { useEffect, useRef, useState } from 'react';
import Chat from '../assets/icons/VoiceConversation/chat.png';
import SettingsGear from '../assets/icons/SettingsGear';
import { agentState } from '../recoil/agent/atom';
import { sendMessageToAurora } from '../services/api';

import { AnimatePresence, motion, useAnimate } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import SettingsCard from '../components/SettingsCard';
import { isVoiceSettingsOpen, voicePersona } from '../recoil';

import Mic from '../assets/icons/VoiceConversation/mic.png';
import Pause from '../assets/icons/VoiceConversation/pause.png';
import Stop from '../assets/icons/VoiceConversation/stop.png';

import Circle_default from '../assets/icons/VoiceConversation/Circle_default.svg';
import Circle_error from '../assets/icons/VoiceConversation/Circle_error.svg';
import Circle_listen from '../assets/icons/VoiceConversation/Circle_listen.svg';

import MicRingMedium from '../assets/icons/VoiceConversation/mic-ring-medium.png';
import MicRingSmall from '../assets/icons/VoiceConversation/mic-ring-small.png';

import Talking from '../assets/icons/VoiceConversation/Talking';
import Thinking from '../assets/icons/VoiceConversation/Thinking';

import splashAnimation from '../assets/icons/VoiceConversation/splash_rings.png';

import { useAudioRecorder } from 'react-audio-voice-recorder';
import SignalBarsLeft from '../components/SignalBarsLeft';
import SignalBarsRight from '../components/SignalBarsRight';
import { chatHistoryAtom } from '../recoil/chatHistory/atom';

import { isMobile } from 'react-device-detect';
import { useQuery } from '../services/utils';

const SPEECH_KEY = process.env.REACT_APP_SPEECH_KEY;
const SPEECH_REGION = process.env.REACT_APP_SPEECH_REGION;
let sessionId = uuidv4();

interface CustomPlayer {
  play: () => void;
  pause: () => void;
  stop: () => void;
  onEnd: (callback: () => void) => void;
}

const VoiceConversation: React.FC = () => {
  const [currentAgent, setCurrentAgent] = useRecoilState(agentState);

  const recorder = useAudioRecorder();
  const [showHello, setShowHello] = useState(true);
  const [showYouCanSay, setShowYouCanSay] = useState(false);
  const [convoStarted, setConvoStarted] = useState(false);
  const [, setIsThinking] = useState(false);
  const [showMic, setShowMic] = useState(false);

  const [aiIdle, setAIIdle] = useState(true);
  const [aiListening, setAiListening] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [aiTalking, setAiTalking] = useState(false);
  const [aiError, setAiError] = useState(false);

  const [aiPaused, setAiPaused] = useState(false);
  const aiPausedRef = useRef<boolean>(false);

  const [isConvoReady, setIsConvoReady] = useState(false);
  const [chatHistory, setChatHistory] = useRecoilState(chatHistoryAtom);

  const [isRecognizerActive, setIsRecognizerActive] = useState<boolean>(false);

  const synthesizerRef = useRef<SpeechSynthesizer | undefined>();
  const recognizerRef = useRef<SpeechRecognizer>();

  const playerRef = useRef<CustomPlayer>();
  const audioRef = useRef<HTMLAudioElement>();
  const [testCounter, setTestCounter] = useState<number>(0);
  const interruptRef = useRef<boolean>(false);

  const isClosedRef = useRef<boolean>(false);

  const [scope, animate] = useAnimate();
  const [talkingScope, talkingAnimate] = useAnimate();

  const navigate = useNavigate();

  const settingsCardOpen = useRecoilValue(isVoiceSettingsOpen);
  const setIsSettingsCardOpen = useSetRecoilState(isVoiceSettingsOpen);

  const voicePersonaOption = useRecoilValue(voicePersona);

  const handleToggleSettingsCard = () => {
    setIsSettingsCardOpen((prevState) => !prevState); // Toggle the state
  };

  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  //SPEECH AND AUDIO CONFIG
  const speechConfig: SpeechConfig = SpeechConfig.fromSubscription(
    SPEECH_KEY || '',
    SPEECH_REGION || ''
  );

  speechConfig.speechRecognitionLanguage = 'en-US';
  speechConfig.speechSynthesisVoiceName = voicePersonaOption.AzureCode;
  speechConfig.speechSynthesisOutputFormat =
    SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;

  const audioConfigRec: AudioConfig = AudioConfig.fromDefaultMicrophoneInput(); //listening

  const voiceToChat = () => {
    // stop any ongoing speech and close resources
    onClickInterrupt();

    const currentUrl = new URL(window.location.href);
    const currentPath = currentUrl.pathname;
    console.log('CURRENT URL: ', currentUrl);

    const newPath = currentPath.replace('voice', 'chat');
    const newUrl = `${newPath}${currentUrl.search}`;
    console.log('NEW URL: ', newUrl);

    navigate(newUrl);
  };

  let onEndCallback = () => {
    if (synthesizerRef.current) {
      console.log('ON END CALLBACK -- handleManualPlayback');
      synthesizerRef.current?.close();
      synthesizerRef.current = undefined;
      resetConversation();
    }
  };

  const requestMicrophonePermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone permissions granted');
    } catch (error) {
      console.error('Microphone permissions denied', error);
      setAiError(true);
    }
  };

  const initializeRecognizer = () => {
    console.log('HERE FOUR');
    try {
      const recognizer: SpeechRecognizer = new SpeechRecognizer(
        speechConfig,
        audioConfigRec
      );
      recognizerRef.current = recognizer;

      recognizer.recognizing = (s, e) => {
        try {
          if (
            e.result.reason === ResultReason.RecognizingSpeech &&
            !aiPausedRef.current
          ) {
            console.log(`Recognizing: ${e.result.text}`);
          }
        } catch (error) {
          console.error('Error during recognizing: ', error);
        }
      };

      recognizer.recognized = async (s, e) => {
        try {
          if (
            e.result.reason === ResultReason.RecognizedSpeech &&
            !aiPausedRef.current
          ) {
            console.log(`User done talking`);

            recognizer.stopContinuousRecognitionAsync(() => {
              recognizer.close();
              recognizerRef.current = undefined;
            });

            setIsRecognizerActive(false);
            setShowYouCanSay(false);
            if (convoStarted === false) {
              setConvoStarted(true);
            }
            recorder.stopRecording();
            console.log(e.result.text);

            setChatHistory((prevChatHistory) => [
              ...prevChatHistory,
              { entity: 'human', content: `${e.result.text}` },
            ]);
            setAiListening(false);
            setAiThinking(true);

            const response = await sendMessageToAurora(
              e.result.text,
              sessionId,
              currentAgent || 'spark',
              []
            );
            const responseData = await response.json();
            if (synthesizerRef.current && !aiPausedRef.current) {
              MairSpeak(
                synthesizerRef.current,
                responseData.message,
                e.result.text
              );
            }
            console.log(responseData.message);
          } else if (e.result.reason === ResultReason.Canceled) {
            setAiPaused(true);
            aiPausedRef.current = true;

            setAiListening(false);
            console.log('Pausing speech listener');
          }
        } catch (err) {
          console.error('Error during recognized event:', err);
          resetConversation(); // Reset conversation on error
        }
      };

      recognizer.canceled = (s, e) => {
        console.log(`Canceled: ${e.reason}`);
        setAiError(true);
        setAiListening(false);
        recorder.stopRecording();
        resetConversation();
      };

      recognizer.sessionStopped = (s, e) => {
        console.log(`Session stopped`);
        setAiListening(false);
        recorder.stopRecording();
      };
      console.log('HERE 5');
    } catch (err) {
      console.log('Error initializing recognizer: ', err);
      resetConversation(); // Reset conversation on error
    }
  };

  const initializeSynthesizer = () => {
    try {
      const stream = AudioOutputStream.createPullStream();
      const audioConfigSpeak = AudioConfig.fromStreamOutput(stream);
      const newSynthesizer = new SpeechSynthesizer(
        speechConfig,
        audioConfigSpeak
      );
      synthesizerRef.current = newSynthesizer;

      synthesizerRef.current.SynthesisCanceled = (s, e) => {
        console.log('SYNTHESIZER CANCELED: ');
      };

      synthesizerRef.current.synthesisStarted = (s, e) => {
        console.log('SYNTHESIZER STARTED');
        setAiTalking(true);
        if (recognizerRef.current) {
          recognizerRef.current.stopContinuousRecognitionAsync(() => {
            recognizerRef.current?.close();
            recognizerRef.current = undefined;
          });
        }
      };

      synthesizerRef.current.synthesisCompleted = (s, e) => {
        console.log('SYNTHESIZER COMPLETED');
      };
    } catch (err) {
      console.error('Error initializing synthesizer: ', err);
      resetConversation(); // Reset conversation on error
    }
  };

  const pauseAIListener = () => {
    console.log('INSIDE PAUSE AI LISTENER');
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync();
      setAiPaused(true);
      setShowYouCanSay(false);

      setAiListening(false);
      setIsRecognizerActive(false);
      console.log('Paused speech listener');
    }
  };

  const resumeAIListener = () => {
    if (recognizerRef.current) {
      recognizerRef.current.startContinuousRecognitionAsync();
      console.log('Resumed continuous recognition');
      setAiPaused(false);
      setAiListening(true);
      setIsRecognizerActive(true);
    }
  };

  function onClickInterrupt() {
    console.log('ON CLICK INTERRUPT ---- INTERUPTING TALKING + RESET');
    console.log('CLOSED STATE', isClosedRef.current);
    interruptRef.current = true;
    if (playerRef.current && audioRef.current) {
      playerRef.current.pause();
      playerRef.current.stop();
    }
  }

  function stopSpeechRecognition() {
    if (recognizerRef.current && isRecognizerActive) {
      console.log(`STOPPING SPEECH RECOGNITION`);
      recognizerRef.current.stopContinuousRecognitionAsync(() => {
        recognizerRef.current?.close();
        recognizerRef.current = undefined;
      });

      setAiListening(false);
      setIsRecognizerActive(false);
    }
  }

  function StartSpeechRecognition() {
    setShowYouCanSay(false);
    console.log('HERE THREE');
    initializeRecognizer();
    initializeSynthesizer();

    if (isRecognizerActive) {
      console.log('TRUE');
      return;
    }
    console.log(`STARTING SPEECH RECOGNITION`);

    setAIIdle(false);
    setAiTalking(false);
    setAiListening(true);
    setShowYouCanSay(false);
    recorder.startRecording();

    console.log('Speak into the mic.');

    const recognitionTimeout = setTimeout(() => {
      if (isRecognizerActive) {
        console.log('Recognition timeout reached');
        setAiError(true);
        resetConversation();
      }
    }, 10000); // Set the timeout duration as needed

    if (recognizerRef.current) {
      recognizerRef.current.startContinuousRecognitionAsync(
        () => {
          if (!isRecognizerActive) {
            setAiListening(true);
            console.log('Continuous recognition started');
            setIsRecognizerActive(true);
            clearTimeout(recognitionTimeout); // Clear the timeout if recognition starts successfully
          }
        },
        (err) => {
          console.error('Error starting continuous recognition:', err);
          setAiError(true);
          clearTimeout(recognitionTimeout);
          resetConversation();
        }
      );
    }
  }

  function onClickListening(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) {
    // console.log('Element that triggered the onClickListening:', event.target);
    // console.log('ON CLICK MAIN BUTTON ---- START RECOGNITION');

    setShowYouCanSay(false);

    // Only when the user clicks mic button
    if (aiIdle) {
      if (audioRef.current) {
        // Reset the audio element
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.muted = true;
      } else {
        const audio = new Audio();
        audio.id = testCounter.toString();
        let num = testCounter + 1;
        setTestCounter(num);

        audio.muted = true;
        audioRef.current = audio;
      }
    }

    let audio = audioRef.current;
    console.log('AUDIO REF: ', audio);
    console.log('AUDIO REF: ', audioRef.current);
    if (audio && aiIdle) {
      const customPlayer: CustomPlayer = {
        play: () => {
          if (audio) {
            audio.autoplay = true;
            audio.muted = false;
            let promise = audio.play();
            if (promise !== undefined) {
              promise
                .then((_) => {
                  console.log('AUTOPLAY STARTED');
                  if (audio) console.log('AUDIO ID - USING: ', audio.id);
                })
                .catch((error) => {
                  console.log('AUTOPLAY PREVENTED');
                  synthesizerRef.current?.close();
                  console.log('ERROR: ', error);
                });
            }
          }
        },
        pause: () => {
          if (audio) audio.pause();
        },
        stop: () => {
          if (audio) {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = true;
            onEndCallback();
          }
        },
        onEnd: (callback) => {
          if (audio) {
            audio.muted = true;
            audio.onended = callback;
          }
        },
      };
      customPlayer.onEnd(onEndCallback);
      playerRef.current = customPlayer;
    }
    StartSpeechRecognition();
  }

  function onClickCancel(event: any) {
    onEndCallback();
  }

  function resetConversation() {
    console.log('RESETTING CONVERSATION');
    stopSpeechRecognition();
    setIsThinking(false);
    setAiListening(false);
    setAiThinking(false);
    setAiTalking(false);
    setAiError(false);
    setAiPaused(false);
    setIsConvoReady(true);
    setShowMic(true);
    setAIIdle(true);

    aiPausedRef.current = false;
  }

  function MairSpeak(
    Synthesizer: SpeechSynthesizer | undefined,
    response?: string,
    chat?: string
  ) {
    try {
      setInitialLoad(false);

      setIsThinking(false);
      console.log(
        `MAIR Synthesizer ref: ${Synthesizer === synthesizerRef.current}`
      );
      Synthesizer?.speakTextAsync(
        response || 'hello',
        async (result) => {
          try {
            setAiThinking(false);
            setAiTalking(true);

            if (result.reason === ResultReason.SynthesizingAudioCompleted) {
              setChatHistory((prevChatHistory) => [
                ...prevChatHistory,
                { entity: 'ai', content: response },
              ]);

              await requestMicrophonePermissions();

              console.log(result.audioDuration);
              handleManualPlayback(
                result.audioData,
                resetConversation,
                StartSpeechRecognition
              );

              // Convert audioDuration from ticks to milliseconds
              const audioDurationMs = result.audioDuration / 10000; // 100 ticks per nanosecond

              // Wait for the duration of the synthesized audio
              await new Promise((resolve) =>
                setTimeout(resolve, audioDurationMs)
              );

              if (isClosedRef.current || !synthesizerRef.current) {
                console.log('AVODING CALLING close() again');
                isClosedRef.current = false;
                return;
              }
            } else if (result.reason === ResultReason.Canceled) {
              Synthesizer?.close(() => {
                resetConversation();
              });
            }
          } catch (err) {
            console.error('Error during speakTextAsync: ', err);
            resetConversation(); // Reset conversation on error
          }
        },
        (err: any) => {
          console.log('Error: ', err);
          resetConversation(); // Reset conversation on error
        }
      );
    } catch (err) {
      console.log('Error in MairSpeak: ', err);
      resetConversation(); // Reset conversation on error
    }
  }

  const handleManualPlayback = (
    audioData: ArrayBuffer,
    resetConversation: () => void,
    startSpeechRecognition: () => void
  ) => {
    try {
      const audioBlob = new Blob([audioData], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        console.log('UPDATING AUDIO URL');
      }

      let audio = audioRef.current;
      let customPlayer = playerRef.current;
      if (audio && customPlayer) {
        audio.autoplay = true; //ADDED
        customPlayer.play();
        console.log('CUSTOM PLAYER PLAYING');
      }
    } catch (err) {
      console.error('Error in handleManualPlayback: ', err);
      resetConversation(); // Reset conversation on error
    }
  };

  useEffect(() => {
    if (aiIdle) {
      setTimeout(() => {
        setShowHello(false);
        setShowYouCanSay(true);
      }, 2000);

      setTimeout(() => {
        setIsConvoReady(true);
        setShowMic(true);
      }, 5000);
    }
  }, [aiIdle]);

  const getActionText = () => {
    switch (true) {
      case aiListening:
        return <div>I'm Listening</div>;
      case aiThinking:
        return "I'm thinking";
      case aiTalking:
        return 'Tap to interrupt';
      case aiIdle:
        return <div>Tap to start speaking</div>;
      default:
        return '';
    }
  };

  const getIcon = () => {
    if (aiListening) {
      return <img className="h-8 w-6" src={Pause} alt="mic" />;
    } else if (aiThinking) {
      return <img className="h-8 w-6" src={Stop} alt="pause" />;
    } else if (aiTalking) {
      return <img className="h-8 w-6" src={Stop} alt="play" />;
    } else if (aiIdle) {
      return <img className="h-8 w-6" src={Mic} alt="mic" />;
    } else {
      return null; // Return null if none of the conditions match
    }
  };

  const query = useQuery();
  const agent = query.get('agent');

  useEffect(() => {
    let currAgent = '';

    if (agent) {
      switch (agent) {
        case 'shell':
          currAgent = 'shell';
          break;
        case 'spark':
          currAgent = 'spark';
          break;
        case 'laila-sxsw':
          currAgent = 'sxsw';
          break;
        case 'laila-oracle':
          currAgent = 'oracle';
          break;
        case 'laila-ny_genai':
          currAgent = 'ny_genai';
          break;
        case 'aslc_france':
          currAgent = 'aslc_france';
          break;
        case 'spark-houston':
          currAgent = 'houston';
          break;
        case 'spark-atlanta':
          currAgent = 'atlanta';
          break;
        case 'spark-new_york':
          currAgent = 'new_york';
          break;
        case 'spark-chicago':
          currAgent = 'chicago';
          break;
        case 'robin':
          currAgent = 'robin';
          break;
        case 'spark-hess':
          currAgent = 'hess';
          break;
        case 'felix':
          currAgent = 'felix';
          break;
        case 'mair':
          currAgent = 'mair';
          break;
        case 'mair-gpt':
          currAgent = 'mair-gpt';
          break;
        case 'spark-ihub':
          currAgent = 'ihub';
          break;
        default:
          // Handle the case where the agent parameter is not supported
          console.error('Invalid agent parameter:', agent);
          navigate('*');
          break;
      }
    } else {
      console.error('Please set an agent parameter');
      navigate('*');
    }

    if (currAgent !== '' && chatHistory.length === 2) {
      setCurrentAgent(currAgent);
      // Voice conversation uses no intro messages; chat conversation uses initializeChatHistory(currAgent).
      setChatHistory([]);
    }
  }, [agent, navigate, setCurrentAgent, setChatHistory, chatHistory.length]);

  //function to update Circle image based on AI state
  function getCirclePng(): string | undefined {
    if (aiListening) {
      return Circle_listen;
    } else if (aiError) {
      return Circle_error;
    } else {
      return Circle_default;
    }
  }

  return (
    <div className="relative h-full w-screen overflow-hidden bg-gradient-to-br from-[#5570f1] to-[#9561e5]">
      {settingsCardOpen && (
        <div className=" absolute bottom-0 z-50 flex w-screen p-5 ">
          <SettingsCard />
        </div>
      )}

      <div className="app-container flex h-screen w-full flex-col  items-center justify-center overflow-hidden ">
        <>
          <AnimatePresence>
            {showYouCanSay && initialLoad && (
              <motion.div
                className=" absolute top-4  w-full px-4 md:px-20 lg:min-w-[600px] lg:max-w-[1200px] lg:px-[200px] "
                key={0}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 2 }}
                exit={{ opacity: 0 }}
              >
                <div className=" flex h-full w-full flex-col gap-6 rounded-xl bg-white bg-opacity-10 p-6">
                  <div className="Header flex flex-col gap-2 font-poppins text-base font-normal leading-normal tracking-wide text-white opacity-70">
                    {' '}
                    You can say
                  </div>

                  <div className=" flex w-full flex-1 flex-col gap-2">
                    <div className="Header WhatCanYouDo flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-white">
                      "What can you do?"
                      <hr />
                    </div>
                    {currentAgent === 'mair' || currentAgent === 'mair-gpt' ? (
                      <>
                        <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-white">
                          "What is the 2025 Tech Vision?"
                          <hr />
                        </div>
                        <div className="Header flex flex-col gap-2 font-poppins text-lg font-normal leading-relaxed tracking-tight text-white">
                          "What is Innovation?"
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-white">
                          "What is the trend 'Meet my Agent'?"
                          <hr />
                        </div>
                        <div className="Header flex flex-col gap-2 font-poppins text-lg font-normal leading-relaxed tracking-tight text-white">
                          "What is the Liquid Studio?"
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/*"Show hello message here"*/}
          {showHello && (
            <AnimatePresence>
              <div className=" flex h-[300px] w-full items-center justify-center">
                <img
                  src={splashAnimation}
                  alt="Rings and Stars Animation"
                  className="background-animation"
                />
                <AnimatePresence>
                  <motion.div
                    key={1}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeOut', duration: 3 }}
                    exit={{ opacity: 0 }}
                    className="Hello absolute left-1/2 h-16 w-72 -translate-x-1/2 transform text-center font-poppins text-4xl  font-light leading-10 text-white"
                  >
                    <p className="text-center font-poppins text-4xl font-[275] leading-[54px] tracking-[1px]">
                      Hello!
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </AnimatePresence>
          )}
          {/*"Show circle and animation here"*/}
          {isConvoReady &&
            (aiListening || aiTalking || aiThinking || aiPaused) && (
              <AnimatePresence>
                <motion.div
                  key={2}
                  className="Box absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform "
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 2 }}
                >
                  {aiIdle && <div></div>}
                  {aiThinking && (
                    <AnimatePresence>
                      <motion.div
                        className="Thinking left-[70px] top-[91px] mb-36 h-[150px] w-[190px] gap-0 py-0 pl-0 pr-[0.05px]"
                        key={3}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ ease: 'easeOut', duration: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Thinking />
                      </motion.div>
                    </AnimatePresence>
                  )}
                  {aiTalking && !aiThinking && (
                    <AnimatePresence>
                      <motion.div
                        className="Talking mb-36 flex items-center justify-center"
                        ref={talkingScope}
                        key={4}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ ease: 'easeOut', duration: 2 }}
                        exit={{ opacity: 0 }}
                        onAnimationComplete={() => {
                          talkingAnimate(
                            talkingScope.current,
                            { scale: [1, 0.95, 1.05, 1] },
                            {
                              duration: 2,
                              repeat: Infinity,
                              ease: 'linear',
                            }
                          );
                        }}
                      >
                        <Talking />
                      </motion.div>
                    </AnimatePresence>
                  )}
                </motion.div>

                <div
                  className="left-[11px] top-36 mb-36 h-[310px] w-[310px] gap-0"
                  id="rings"
                >
                  <motion.img
                    key={5}
                    className="left-[11px] top-36"
                    ref={scope}
                    transition={{
                      duration: 4,
                    }} // Adjust duration as needed
                    src={getCirclePng()}
                    alt="gradient circle"
                    onAnimationComplete={() => {
                      animate(
                        scope.current,
                        { rotate: [0, 90, 180, 360] },
                        {
                          duration: 5,
                          repeat: Infinity,
                          ease: 'linear',
                          times: [0, 2, 4, 6],
                        }
                      );
                    }}
                  />
                </div>
              </AnimatePresence>
            )}

          {/*"show mic and audio visualizer here"*/}
          {isConvoReady && (
            <div className="MicAndVisualizerContainer absolute bottom-6 flex h-[200px] w-full flex-col items-center  justify-center p-4 pt-10 font-poppins font-medium text-white">
              <div>{getActionText()}</div>
              <div className=" flex items-center justify-center">
                <button className="MicButtonContainer">
                  <div className=" flex h-[200px] w-[250px] items-center justify-center">
                    {/* IDLE */}
                    {aiIdle && showMic && (
                      <div
                        id="mic"
                        onClick={(event) => onClickListening(event)}
                        className=" flex h-full w-full items-center justify-center"
                      >
                        <div className=" absolute  left-1/2 -translate-x-1/2 transform">
                          <motion.img
                            initial={{ width: 150, height: 150 }}
                            animate={{
                              width: [150, 100, 150],
                              height: [150, 100, 150],
                            }}
                            transition={{
                              duration: 10,
                              repeat: Infinity,
                              ease: 'linear',
                              times: [0, 4, 8, 12],
                            }}
                            src={MicRingMedium}
                            alt="mic"
                          />
                        </div>
                        <div className=" absolute  left-1/2 -translate-x-1/2 transform">
                          <motion.img
                            initial={{ width: 100, height: 100 }}
                            animate={{
                              width: [100, 50, 100],
                              height: [100, 50, 100],
                            }}
                            transition={{
                              duration: 10,
                              repeat: Infinity,
                              ease: 'linear',
                              times: [0, 4, 8, 12],
                            }}
                            src={MicRingSmall}
                            alt="mic"
                          />
                        </div>
                      </div>
                    )}

                    {/* LISTENING -> PAUSED */}
                    {aiListening && (
                      <div
                        className={`flex h-3/5 w-full justify-center`}
                        onClick={pauseAIListener}
                      >
                        <SignalBarsLeft />
                        <motion.div
                          className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                          onClick={pauseAIListener}
                        >
                          <button>{getIcon()}</button>
                        </motion.div>
                        <SignalBarsRight />
                      </div>
                    )}

                    {/* RESUME */}
                    {aiPaused && (
                      <motion.div
                        className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                        onClick={resumeAIListener}
                      >
                        <button>{getIcon()}</button>
                      </motion.div>
                    )}

                    {/* CANCEL WHEN THINKING */}
                    {aiThinking && (
                      <motion.div
                        className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                        onClick={(event) => onClickCancel(event)}
                      >
                        <button>{getIcon()}</button>
                      </motion.div>
                    )}

                    {/* START LISTENING - FROM IDLE */}
                    {aiIdle && (
                      <motion.div
                        className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                        onClick={(event) => onClickListening(event)}
                      >
                        <button>{getIcon()}</button>
                      </motion.div>
                    )}

                    {/* TALKING - INTERUPT BUTTON */}
                    {aiTalking && (
                      <motion.div
                        className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                        onClick={(event) => onClickInterrupt()}
                      >
                        <button>{getIcon()}</button>
                      </motion.div>
                    )}

                    {/* AI ERROR - RESTART BUTTON */}
                    {aiError && (
                      <motion.div
                        className=" absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform items-center justify-center rounded-full bg-white"
                        onClick={(event) => onClickInterrupt()}
                      >
                        <button>{getIcon()}</button>
                      </motion.div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          )}
        </>
      </div>
      {!isMobile && (
        <button
          className="ChatContainer absolute bottom-3 right-3"
          onClick={() => voiceToChat()}
        >
          <img src={Chat} alt="chat" className="h-12 w-12" />
        </button>
      )}
      {!isMobile && (
        <button
          className="ChatContainer absolute bottom-3 right-20"
          onClick={handleToggleSettingsCard}
        >
          <SettingsGear />
        </button>
      )}
    </div>
  );
};

export default VoiceConversation;
